<template>
  <div class="hero-rotator" @click="handleClick" @touchstart.passive="handleClick">
    <!-- faceA: starts as front (visible at 0°) — LCP element -->
    <div ref="faceARef" class="hero-face">
      <picture>
        <img :src="faceAImg.src" :srcset="faceAImg.srcset" :alt="faceAImg.alt ?? alt" class="hero-img" loading="eager" fetchpriority="high" decoding="async">
      </picture>
    </div>
    <!-- faceB: delayed render — don't compete with LCP image on bandwidth -->
    <div ref="faceBRef" class="hero-face">
      <picture v-if="faceBReady">
        <img :src="faceBImg.src" :srcset="faceBImg.srcset" :alt="faceBImg.alt ?? alt" class="hero-img" loading="lazy" fetchpriority="low" decoding="async">
      </picture>
    </div>
  </div>
</template>

<script setup lang="ts">
export interface HeroImageSource {
  src: string
  alt?: string
  srcset?: string
}

interface Props {
  images: HeroImageSource[]
  alt?: string
  interval?: number
}

const props = withDefaults(defineProps<Props>(), {
  alt: 'Hero image',
  interval: 7000,
})

const faceAIdx = ref(0)
const faceBIdx = ref(1)
const isFaceAFront = ref(true)
const faceBReady = ref(false)

const faceAImg = computed(() => props.images[faceAIdx.value] ?? props.images[0]!)
const faceBImg = computed(() => props.images[faceBIdx.value] ?? props.images[0]!)

const faceARef = ref<HTMLElement | null>(null)
const faceBRef = ref<HTMLElement | null>(null)

const isRotating = ref(false)
const clickQueue = ref(0)
const HALF = 350

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

async function performRotation() {
  if (isRotating.value || !faceARef.value || !faceBRef.value) return
  isRotating.value = true

  const front = isFaceAFront.value ? faceARef.value : faceBRef.value
  const back  = isFaceAFront.value ? faceBRef.value : faceARef.value

  front.style.transition = `transform ${HALF}ms ease-in`
  front.style.transform = 'rotateY(90deg)'
  await delay(HALF)

  back.style.transition = `transform ${HALF}ms ease-out`
  back.style.transform = 'rotateY(0deg)'
  await delay(HALF)

  front.style.transition = 'none'
  front.style.transform = 'rotateY(-90deg)'
  void front.offsetHeight // flush transition:none before next animation frame

  const wasAFront = isFaceAFront.value
  isFaceAFront.value = !isFaceAFront.value

  const justShownIdx = wasAFront ? faceBIdx.value : faceAIdx.value
  const nextBackIdx = (justShownIdx + 1) % props.images.length

  if (isFaceAFront.value) {
    faceBIdx.value = nextBackIdx
  } else {
    faceAIdx.value = nextBackIdx
  }

  isRotating.value = false

  if (clickQueue.value > 0) {
    clickQueue.value--
    performRotation()
  }
}

function handleClick() {
  if (isRotating.value) {
    clickQueue.value++
  } else {
    performRotation()
  }
}

let intervalId: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  if (faceBRef.value) {
    faceBRef.value.style.transform = 'rotateY(-90deg)'
  }
  // Start auto-rotation only after 10s so LCP finalizes on the initial slide before any new content appears
  setTimeout(() => {
    faceBReady.value = true
    intervalId = setInterval(() => {
      if (!isRotating.value) performRotation()
    }, props.interval)
  }, 10000)
})

onUnmounted(() => {
  if (intervalId) clearInterval(intervalId)
})
</script>

<style scoped>
.hero-rotator {
  position: relative;
  width: 100%;
  height: clamp(320px, 55vw, 790px);
  perspective: 1000px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.hero-face {
  position: absolute;
  inset: 0;
}

picture {
  display: block;
  width: 100%;
  height: 100%;
}

.hero-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: bottom center;
}

@media (max-width: 900px) {
  .hero-rotator {
    height: clamp(260px, 70vw, 520px);
  }

  .hero-img {
    object-position: top center;
  }
}

@media (max-width: 480px) {
  .hero-rotator {
    height: clamp(240px, 80vw, 420px);
  }
}
</style>
